/**
 * Lot B step 13 — Agency « Lancer » popup (00b DQ13: the one agency manual path).
 * Mounted by the axis page's agency « Nouvel audit » and its empty-state CTA; a tenant
 * still on 'legacy' is handed back to the page's per-axis dialog through `onLegacy`.
 *
 * Uses launchModel.ts + promptLaunchesApi from services/prompt-launches.ts.
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AXIS_COLOR } from './promptStudioModel';
import {
  launchReducer, canLaunch, launchClosed, isLegacyDispatch, previewOutcome, launchAxes,
  type ManualLaunchMode, type PreviewSettled,
  type PromptLaunchStatus as LaunchModelStatus,
} from './launchModel';
import { promptLaunchesApi } from '../../services/prompt-launches';

interface LaunchDialogProps {
  open: boolean;
  onClose: () => void;
  brandId: string;
  /** Brand-wide whatever page opened it: the quiet line under the title (UI-SPEC §2.27). */
  brandName: string;
  onLaunched?: () => void;
  /**
   * Rollout fallback: a 409 PROMPT_DISPATCH_LEGACY (preview or send) calls this instead
   * of showing the `.launch.legacy` line, so the caller can open its per-axis dialog.
   */
  onLegacy?: () => void;
}

const INITIAL = {
  phase: 'loading' as const,
  previews: {} as Record<string, any>,
  previewFailed: false,
  mode: null as ManualLaunchMode | null,
  idem: null as any,
};

export default function LaunchDialog({ open, onClose, brandId, brandName, onLaunched, onLegacy }: LaunchDialogProps) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(launchReducer, INITIAL as any);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onLegacyRef = useRef(onLegacy);
  onLegacyRef.current = onLegacy;

  useEffect(() => {
    if (!open) return;
    const settle = (p: Promise<any>): Promise<PreviewSettled> =>
      p.then((value) => ({ value }), (err) => ({ value: null, err }));
    Promise.all([
      settle(promptLaunchesApi.preview(brandId, 'all')),
      settle(promptLaunchesApi.preview(brandId, 'missing')),
    ]).then(([all, missing]) => {
      const out = previewOutcome(all, missing, !!onLegacyRef.current);
      if (out.kind === 'handBack') onLegacyRef.current?.();
      else if (out.kind === 'failed') dispatch({ type: 'previewsFailed', code: out.code });
      else dispatch({ type: 'previewsLoaded', previews: out.previews });
    });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, brandId]);

  const handleSubmit = useCallback(async (confirmReplace = false) => {
    const freshKey = crypto.randomUUID();
    const mode = state.mode ?? 'missing';
    dispatch({ type: confirmReplace ? 'confirmReplace' : 'submit', freshKey });
    try {
      const result = await promptLaunchesApi.create(brandId, { mode, confirmReplace }, freshKey);
      dispatch({ type: 'created', launchId: result.launchId });
      pollRef.current = setInterval(async () => {
        if (!document.hidden) {
          const status = await promptLaunchesApi.get(brandId, result.launchId).catch(() => null);
          if (status) {
            dispatch({ type: 'polled', status: status as unknown as LaunchModelStatus });
            if (launchClosed(status as unknown as LaunchModelStatus)) {
              clearInterval(pollRef.current!);
              onLaunched?.();
            }
          }
        }
      }, 5000);
    } catch (err: any) {
      if (onLegacyRef.current && isLegacyDispatch(err)) {
        onLegacyRef.current();
        return;
      }
      dispatch({ type: 'createFailed', err });
    }
  }, [brandId, state.mode, onLaunched]);

  const preview = state.mode && state.previews?.[state.mode];
  const isLoading = state.phase === 'loading' || state.phase === 'submitting';
  const inProgress = state.phase === 'progress';
  const isLegacy = state.phase === 'legacy';

  return (
    <Dialog open={open} onClose={isLoading || inProgress ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {/* i18n: promptStudio.launch.title */}
        Lancer une mesure
      </DialogTitle>
      <Typography sx={{ px: 3, mt: -1, mb: 1, fontSize: 12.5, color: 'text.secondary' }}>
        {t('promptStudio.launch.brandWide', { brand: brandName })}
      </Typography>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {state.previewFailed && (
          <Alert severity="error">Impossible de charger les figures. Réessayez.</Alert>
        )}

        {state.phase === 'choose' && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              {(['all', 'missing'] as ManualLaunchMode[]).map((m) => (
                <Chip
                  key={m}
                  label={m === 'all' ? 'Tous les prompts' : 'Pas encore mesurés aujourd\'hui'}
                  color={state.mode === m ? 'primary' : 'default'}
                  onClick={() => dispatch({ type: 'pick', mode: m })}
                  variant={state.mode === m ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
            {preview && (
              <Typography variant="body2" color="text.secondary">
                {preview.prompts ?? 0} prompts · {preview.runs ?? 0} runs · {preview.credits ?? 0} cr
              </Typography>
            )}
          </Stack>
        )}

        {state.phase === 'confirmReplace' && (
          <Alert severity="warning">
            Cette mesure remplacera la mesure d'aujourd'hui.
          </Alert>
        )}

        {inProgress && !state.status && (
          <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress />
            <Typography>Mesure en cours…</Typography>
          </Stack>
        )}

        {/* UI-SPEC §2.27 progress: one line per axis, a 6 px bar `done / total`. */}
        {inProgress && state.status && (
          <Stack spacing={1.5} sx={{ py: 1 }}>
            {launchAxes(state.status).map((a) => (
              <Box key={a.axis}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: AXIS_COLOR[a.axis] }} />
                  <Typography variant="body2" fontWeight={600}>{t(`promptStudio.axis.${a.axis}`)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.finished
                      ? a.ok > 0 ? t('promptStudio.launch.axisDone') : t('promptStudio.launch.axisFailed')
                      : a.retrying
                        ? t('promptStudio.launch.retrying')
                        : a.total === null ? t('promptStudio.launch.preparing') : `${a.done} / ${a.total}`}
                  </Typography>
                </Stack>
                <LinearProgress
                  {...(a.total !== null || a.finished
                    ? { variant: 'determinate' as const, value: a.finished ? 100 : Math.min(100, (a.done / Math.max(1, a.total!)) * 100) }
                    : {})}
                  sx={{ height: 6, borderRadius: 3, bgcolor: `${AXIS_COLOR[a.axis]}22`, '& .MuiLinearProgress-bar': { bgcolor: AXIS_COLOR[a.axis] } }}
                />
                {(a.ok > 0 || a.failed > 0) && (
                  <Typography variant="caption" color="text.secondary">
                    {t('promptStudio.launch.counts', { ok: a.ok, failed: a.failed })}
                  </Typography>
                )}
              </Box>
            ))}
            {state.status.status === 'closed' && (
              <Typography variant="body2">{t('promptStudio.launch.done')}</Typography>
            )}
          </Stack>
        )}

        {isLegacy && (
          <Alert severity="info">
            Le nouveau pipeline n'est pas encore activé pour cette marque.
          </Alert>
        )}

        {state.error && (
          <Alert severity="error">{state.error}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        {(state.phase === 'choose' || state.phase === 'loading') && (
          <>
            <Button onClick={onClose}>Annuler</Button>
            <Button
              variant="contained"
              disabled={!canLaunch(state as any)}
              onClick={() => handleSubmit(false)}
            >
              Lancer
            </Button>
          </>
        )}
        {state.phase === 'confirmReplace' && (
          <>
            <Button onClick={() => dispatch({ type: 'back' })}>Annuler</Button>
            <Button variant="contained" color="warning" onClick={() => handleSubmit(true)}>
              Confirmer le remplacement
            </Button>
          </>
        )}
        {(isLegacy || state.phase === 'progress') && (
          <Button onClick={onClose}>Fermer</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
